<template lang="pug">
  .layout
    y-dialog(v-model="showModal" fill-height fluid scrollable persistent transition="fade-transition" max-width="600px" :fullscreen="$vuetify.breakpoint.xsOnly")

      v-card(flat)
        v-card-title.pa-0
          v-row.px-5.pt-3.pb-2(v-if="hasChanges" no-gutters align="center" :style="`background-color: ${$vuetify.theme.themes[$theme].success600}`")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                eva-icon(name="arrow-back-outline" :fill="$vuetify.theme.themes[$theme].basic100" width="24" height="24")
            v-col.ml-3
              p.mb-0.h6.basic100--text {{ $t('save_changes') }}
            v-col.ml-3.shrink
              v-layout
                v-btn.mr-2.my-auto(depressed small outlined color="basic100" @click="clickRevert()")
                  .button-AA-small.basic100--text {{ $t('cancel') }}
                v-btn.my-auto.basic100(depressed small @click="clickSave()" :loading="isLoading")
                  .button-AA-small.primary500--text {{ $t('save') }}
          
          v-row.px-5.pt-3.pb-2(v-else no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                eva-icon(name="arrow-back-outline" :fill="$vuetify.theme.themes[$theme].primary500" width="24" height="24")
            v-col.ml-3
              p.mb-0.h6.primary500--text {{ $t('business_hours') }}

        v-layout.my-6(v-if="isFetching")
          v-row.fill-height.ma-0(align='center' justify='center')
            v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)

        v-card-text.pa-0.pb-12(v-else)
          v-form(ref="form")
            div.px-4
              business-hours(:parentHours.sync="hours" :hoursHeader="true")
              restricted-dates.mt-4(:parentSettings.sync="hours" :restrictedDatesHeader="true")

</template>

<script>
import { mapActions } from 'vuex'
import BusinessHours from '@/components/ordersettings/BusinessHours.vue'
import RestrictedDates from '@/components/ordersettings/RestrictedDates.vue'

export default {
  name: 'LocalOrderSettings',
  components: { BusinessHours, RestrictedDates },
  props: ['open', 'company', 'settings'],
  data: () => ({
    isFetching: false,
    isLoading: false,
    originalHours: null,
    hours: {
      use_same_hours: true
    },
    rules: {
      not_empty: [val => (val || '').length > 0 || 'This field is required'],
      has_quantity: [(v) => !!v || 'This field is required', (v) => v != 0|| 'This field is required'],
      instructions_count: [v => v.length <= 300 || 'Max 300 characters']
    }
  }),
  computed: {
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    hasChanges () {
      if (this.originalHours && JSON.stringify(this.hours) !== this.originalHours) {
        return true
      }
      return false
    }
  },
  watch: {
  },
  mounted () {
    this.fetchBusinessHours()
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    fetchBusinessHours() {
      this.isFetching = true
      this.$axios.get(`/oms/${this.company.slug}/business_hours`)
        .then((response) => {
          this.hours = { ...this.constructObject([...response.data]) }
          this.originalHours = JSON.stringify(this.hours)
        }).catch(() => {})
        .finally(() => { 
          this.isFetching = false
          this.isLoading = false
        })
    },
    formatDate (date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    formatTime (time24) {
      let ts = time24
      const H = + ts.substr(0, 2)
      let h = (H % 12) || 12
      h = (h < 10) ? ('0' + h) : h
      const ampm = H < 12 ? 'am' : 'pm'
      ts = h + ':' + ts.substr(3, 2) + ampm
      return ts
    },
    constructObject(data) {
      const object = {}

      const pickup =  data.filter(f => f.type_name == 'pickup')[0] || { type_name: 'pickup', days: [] }
      object.pickup_hours = this.processHours(pickup.days)
      object.pickup_id = pickup.id

      const delivery = data.filter(f => f.type_name == 'delivery')[0] || { type_name: 'delivery', days: [] }
      object.delivery_hours = this.processHours(delivery.days)
      object.delivery_id = delivery.id

      object.use_same_hours = JSON.stringify(object.pickup_hours) === JSON.stringify(object.delivery_hours)

      object.restricted_dates = [ ...this.settings.restricted_dates ].map(m => { return { date: m, show: false, value: m, display: this.formatDate(m) }})
      return object
    },
    processHours (days) {
      const hours = []
      if (days.length === 0) {
        const data = {
          data: { hours: [{ begin: null, begin_time: null, end: null, end_time: null }], order_limit_enabled: false, order_limit: null},
          days: []
        }
        hours.push(data)
        return hours
      }
      days.forEach(day => {
        const item = { ...day }
        delete item.day
        item.hours.forEach((h, index) => {
          item.hours[index].begin_time = this.formatTime(h.begin)
          item.hours[index].end_time = this.formatTime(h.end)
        })
        const id = JSON.stringify(item)
        const matchedItem = hours.filter(f => JSON.stringify(f.data) === id)[0]
        if (hours.length > 0 && matchedItem) {
          const index = hours.indexOf(matchedItem)
          hours[index].days.push(day.day)
        } else {
          const days = []
          days.push(day.day)
          const data = {
            data: item,
            days: days
          }
          hours.push(data)
        }
      })
      return hours
    },
    clickRevert () {
      this.hours = { ...JSON.parse(this.originalHours) }
    },
    getHourList(hours) {
      const list = []
      hours.forEach(hour => {
        hour.days.forEach(item => {
          const data = {
            ...hour.data,
            day: item
          }
          list.push(data)
        })
      })
      return list
    },
    async clickSave() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      let success = true
      const delivery = { 
        id: this.hours.delivery_id,
        type_name: 'delivery',
        days: this.getHourList(this.hours.delivery_hours)
      }
      success = await this.saveData(delivery)
      if (this.hours.use_same_hours) {
        delivery.id = this.hours.pickup_id
        delivery.type_name = 'pickup'
        success = await this.saveData(delivery)
      } else {
        const pickup = { 
          id: this.hours.pickup_id,
          type_name: 'pickup',
          days: this.getHourList(this.hours.pickup_hours)
        }
        success = await this.saveData(pickup)
      }
      try {
        const data = this.hours.restricted_dates.map(m => { return m.date })
        await this.$axios.patch(`/oms/${this.company.slug}`, { restricted_dates: data })
        this.settings.restricted_dates = data
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        success = false
      }
      if (success) {
        this.$emit("edited")
        this.fetchBusinessHours()
      } else {
        this.isLoading = false
      }
    },
    async saveData (param) {
      if (param.id) {
        try {
          await this.axios.patch(`/oms/${this.company.slug}/business_hours/${param.id}`, param)
        } catch {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          return false
        }
      } else {
        try {
          await this.axios.post(`/oms/${this.company.slug}/business_hours/`, param)
        } catch {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          return false
        }
      }
      return true
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>
